import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import moment from "moment";

const INACTIVITY_SECS = 5 * 60;
const UNRESPONSIVE_SECS = 60 * 60;
const LOW_BATTERY_PCT = 95;

function CardSpinner() {
    return (
        <div style={{
            position: "absolute", inset: 0, background: "rgba(255,255,255,0.8)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10, borderRadius: "inherit",
        }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: "2rem", color: "#6c757d" }} />
        </div>
    );
}

// A true 4-point sparkle (not FontAwesome's 5-point fa-star) — the glyph most
// AI product surfaces (Copilot, Gemini, ...) use to signal "AI-generated".
function Sparkle({ size = 12, color = "currentColor", style }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            style={style}
            aria-hidden="true"
        >
            <polygon
                points="12,0 15,9 24,12 15,15 12,24 9,15 0,12 9,9"
                fill={color}
            />
        </svg>
    );
}

// Scattered sparkle field for the header background — sits between the title
// and the AI Powered badge, varied in size/opacity so it reads as a light
// sprinkle rather than a repeated icon.
const HEADER_SPARKLES = [
    { size: 11, top: 8, left: 150, opacity: 0.5, delay: 0 },
    { size: 5, bottom: 8, left: 195, opacity: 0.3, delay: 0.4 },
    { size: 8, top: 18, left: 232, opacity: 0.4, delay: 0.8 },
    { size: 4, bottom: 16, left: 268, opacity: 0.28, delay: 1.2 },
    { size: 9, top: 10, right: 250, opacity: 0.45, delay: 0.6 },
    { size: 6, bottom: 10, right: 292, opacity: 0.32, delay: 1.0 },
];

function AIAnalysisCard({ deviceData, maintenanceSchedules, shifts, loading }) {
    // Collapsed by default, same shrink-to-header behavior as the other dashboard cards
    // (.collapsed-card + d-none on the body) — see starek.css for why that needs the
    // fixed-height classes gated behind :not(.collapsed-card).
    const [collapsed, setCollapsed] = useState(true);

    const insights = useMemo(() => {
        const items = [];
        const devices = deviceData?.shadowDevices || [];
        const jobScheduleCount = deviceData?.aggregateJobSchedule?._count?.id ?? 0;
        const now = moment();
        const currentSec = now.hours() * 3600 + now.minutes() * 60 + now.seconds();
        const todayDow = now.day();

        // Device health
        let offline = 0, lowBatt = 0, activeJobs = 0;
        const partsInProd = new Set();
        devices.forEach((d) => {
            const secsAgo = moment().diff(moment(d?.device?.lastSeen?.master || 0), "seconds");
            if (secsAgo >= UNRESPONSIVE_SECS) offline++;
            const batt = d?.device?.runtime?.attributes?.battery_level;
            if (batt != null && batt < LOW_BATTERY_PCT) lowBatt++;
            (d.jobSchedules || []).forEach((js) => {
                if (currentSec >= js.start_time && currentSec <= js.end_time) {
                    activeJobs++;
                    if (js.part?.name) partsInProd.add(js.part.name);
                }
            });
        });

        if (offline > 0) {
            items.push({
                icon: "fa-wifi text-danger",
                text: `${offline} device${offline !== 1 ? "s have" : " has"} not reported in over 1 hour.`,
                link: "/devices",
                linkText: "Inspect devices",
                severity: "danger",
            });
        } else if (devices.length > 0) {
            items.push({
                icon: "fa-check-circle text-success",
                text: `All ${devices.length} device${devices.length !== 1 ? "s are" : " is"} reporting normally.`,
                severity: "success",
            });
        }

        if (lowBatt > 0) {
            items.push({
                icon: "fa-battery-quarter text-warning",
                text: `${lowBatt} device${lowBatt !== 1 ? "s are" : " is"} reporting low battery (below ${LOW_BATTERY_PCT}%).`,
                link: "/devices",
                linkText: "View devices",
                severity: "warning",
            });
        }

        if (activeJobs > 0) {
            items.push({
                icon: "fa-industry text-primary",
                text: `${activeJobs} job${activeJobs !== 1 ? "s are" : " is"} running right now across ${partsInProd.size} part${partsInProd.size !== 1 ? "s" : ""}.`,
                severity: "info",
            });
        } else if (jobScheduleCount > 0) {
            items.push({
                icon: "fa-calendar text-muted",
                text: `No jobs are running at this moment. ${jobScheduleCount} schedule${jobScheduleCount !== 1 ? "s are" : " is"} configured.`,
                link: "/job-schedules",
                linkText: "View schedules",
                severity: "secondary",
            });
        }

        // Maintenance due soon
        const dueSoon = (maintenanceSchedules || []).filter((ms) => {
            let attrs = ms.schedule_attributes;
            if (typeof attrs === "string") { try { attrs = JSON.parse(attrs); } catch { attrs = {}; } }
            const from = attrs?.from ? moment(attrs.from) : null;
            if (!from) return false;
            const days = from.startOf("day").diff(moment().startOf("day"), "days");
            return days >= 0 && days <= 7;
        }).length;

        if (dueSoon > 0) {
            items.push({
                icon: "fa-cogs text-warning",
                text: `${dueSoon} maintenance event${dueSoon !== 1 ? "s are" : " is"} due within the next 7 days.`,
                link: "/maintenance",
                linkText: "View schedule",
                severity: "warning",
            });
        }

        // Shift coverage
        if (shifts?.length) {
            const activeShifts = shifts.filter((s) => {
                if (s.runs_on && !(s.runs_on & (1 << todayDow))) return false;
                const end = s.end_time < s.start_time ? 86400 : s.end_time;
                return currentSec >= s.start_time && currentSec <= end;
            });
            if (activeShifts.length > 0) {
                items.push({
                    icon: "fa-user-clock text-info",
                    text: `Currently in ${activeShifts.map((s) => `"${s.name}"`).join(", ")} shift${activeShifts.length !== 1 ? "s" : ""}.`,
                    severity: "info",
                });
            }
        }

        return items;
    }, [deviceData, maintenanceSchedules, shifts]);

    return (
        <div
            className={`card card-outline mb-3 ${collapsed ? "collapsed-card" : ""}`}
            style={{
                borderColor: "#ddd6fe",
                position: "relative",
                overflow: "hidden",
                background: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 35%, #fef3c7 75%, #fef9c3 100%)",
            }}
        >
            {loading && <CardSpinner />}
            <div
                className="card-header"
                style={{
                    background: "transparent",
                    color: "#5b21b6",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                }}
            >
                <style>{`@keyframes twinkle { 0%, 100% { opacity: 0.25; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1.1); } }`}</style>
                {HEADER_SPARKLES.map((s, i) => (
                    <Sparkle
                        key={i}
                        size={s.size}
                        color={`rgba(0,0,0,${s.opacity})`}
                        style={{
                            position: "absolute",
                            ...(s.top != null ? { top: s.top } : { bottom: s.bottom }),
                            ...(s.left != null ? { left: s.left } : { right: s.right }),
                            animation: `twinkle 2.6s ease-in-out infinite ${s.delay}s`,
                        }}
                    />
                ))}
                <h3
                    className="card-title tw-font-bold"
                    style={{ position: "relative", zIndex: 1, margin: 0, display: "flex", alignItems: "center" }}
                >
                    <i className="fas fa-chart-line mr-2" style={{ color: "#5b21b6" }} />
                    Live Insights
                </h3>
                <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
                    <span
                        className="badge badge-pill"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            background: "#7c3aed",
                            color: "#ffffff",
                            fontWeight: "bold",
                            letterSpacing: "0.3px",
                            boxShadow: "0 1px 4px rgba(76,29,149,0.3)",
                        }}
                    >
                        <Sparkle size={11} color="#ffffff" style={{ marginRight: 5 }} />
                        AI Powered
                    </span>
                    <div className="card-tools">
                        <button
                            type="button"
                            className="btn btn-tool"
                            style={{ color: "#000000" }}
                            onClick={() => setCollapsed(!collapsed)}
                        >
                            <i className={`fas ${collapsed ? "fa-plus" : "fa-minus"}`} />
                        </button>
                    </div>
                </div>
            </div>
            <div className={`card-body ${collapsed ? "d-none" : ""}`} style={{ background: "transparent" }}>
                {insights.length === 0 ? (
                    <p className="text-muted text-center small mb-0 py-2">Loading observations…</p>
                ) : (
                    <div className="d-flex flex-column" style={{ gap: 6 }}>
                        {insights.map((item, i) => (
                            <div
                                key={i}
                                className="d-flex align-items-start p-2 rounded"
                                style={{ background: "rgba(255,255,255,0.7)", border: "1px solid rgba(0,0,0,0.07)" }}
                            >
                                <i className={`fa ${item.icon} mt-1 mr-2 flex-shrink-0`} style={{ fontSize: "0.85rem" }} />
                                <span style={{ fontSize: "0.82rem" }}>
                                    {item.text}
                                    {item.link && (
                                        <> <Link to={item.link} className="text-primary ml-1" style={{ fontWeight: 600 }}>{item.linkText} →</Link></>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default AIAnalysisCard;
